// BHP-1044: React Query provider. Wraps the app so any feature can use
// useQuery/useMutation for server-state.
import { type ReactNode, useEffect, useState } from 'react';
import {
  matchQuery,
  type Query,
  QueryClientProvider
} from '@tanstack/react-query';
import { accountKeys } from '../../features/account/application/useCurrentAccount';
import { useActiveCustomerStore } from '../../features/customer/application/activeCustomerStore';
import { hydraKeys } from '../api/useHydra';
import { createQueryClient } from './queryClient';

interface QueryProviderProps {
  children: ReactNode;
}

// What a customer switch must not touch: the signed-in account and the
// customer list itself (the switcher). The backend never narrows either.
const NOT_CUSTOMER_SCOPED = [
  accountKeys.current,
  hydraKeys.resource('customers')
];

function isCustomerScoped(query: Query): boolean {
  return !NOT_CUSTOMER_SCOPED.some((queryKey) =>
    matchQuery({ queryKey }, query)
  );
}

export default function QueryProvider({
  children
}: QueryProviderProps): JSX.Element {
  // One client per app instance, kept stable across renders.
  const [queryClient] = useState(createQueryClient);

  // BHP-1081: refetch all server-state when the active customer changes.
  // Responses are scoped to the active customer via the `x-active-customer`
  // header (Backend2.0) / legacy ApiService, but query keys are not
  // customer-aware — so without this a switch would keep serving another
  // customer's cached data. One subscription covers every current and future
  // query instead of threading the active customer through each query key.
  //
  // BHP-1259: reset, not invalidate, so the previous customer's rows are
  // dropped at once (pages show their loading state), the answers to requests
  // still in flight for that customer no longer land in the cache (the HTTP
  // requests themselves run on: no query passes its signal on), and what is
  // mounted refetches.
  useEffect(
    () =>
      useActiveCustomerStore.subscribe((state, previous) => {
        if (state.activeCustomerUuid !== previous.activeCustomerUuid) {
          void queryClient.resetQueries({ predicate: isCustomerScoped });
        }
      }),
    [queryClient]
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
